/**
 * Public client for the static pages API.
 *
 * Two endpoints:
 *   listNavPages(lang) → page list for the header sub-row
 *   getPage(slug, lang) → single page contents
 *
 * Both accept a language code. Backend handles English fallback
 * silently if the requested language has no translation row.
 */

export async function fetchJson(path) {
  const response = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  let body = null;
  if (isJson) {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  }
  if (!response.ok) {
    const error = new Error(body?.detail || `Request failed: ${response.status}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}


export function listNavPages(language = 'en') {
  const qs = new URLSearchParams({ lang: language });
  return fetchJson(`/api/pages/nav?${qs.toString()}`);
}


/* 0.44.06 - The site's navigation menu, ready to show in `language`:
 * [{id, label, href, external, new_tab}]. */
export function getNavMenu(language = 'en') {
  return fetchJson(`/api/nav-menu?lang=${encodeURIComponent(language)}`);
}

export function listFooterPages(language = 'en') {
  const qs = new URLSearchParams({ lang: language });
  return fetchJson(`/api/pages/footer?${qs.toString()}`);
}


export function getPage(slug, language = 'en') {
  const qs = new URLSearchParams({ lang: language });
  return fetchJson(`/api/pages/${encodeURIComponent(slug)}?${qs.toString()}`);
}


/**
 * 0.36.59 — Fetch whichever page is currently the home (page_type='landing').
 *
 * Used by Landing.jsx (route /) so the home resolves dynamically by
 * role rather than by a hardcoded slug. After an admin runs "Set as
 * home page" on a different page, the role moves; this endpoint
 * returns the new one without any frontend slug bookkeeping.
 */
export function getLandingPage(language = 'en') {
  const qs = new URLSearchParams({ lang: language });
  return fetchJson(`/api/pages/landing-page?${qs.toString()}`);
}


/* 0.42.62 - A flow's shape: its steps, in order. The step PAGES come
 * through getPage above, so a page inside a flow is served exactly as it
 * is on its own. */
export function getFlow(slug) {
  return fetchJson(`/api/flows/${encodeURIComponent(slug)}`);
}

/* 0.42.82 - A step's page, asked for through its flow.
 *
 * A form has no public address of its own, so this is the only way its
 * content reaches a citizen: named by the flow that owns it. */
/* 0.43.89 - The page a flow shows for one of its error situations. */
export function getFlowError(flowSlug, errorId, language = 'en') {
  return fetchJson(`/api/flows/${encodeURIComponent(flowSlug)}`
    + `/errors/${encodeURIComponent(errorId)}?lang=${encodeURIComponent(language)}`);
}

export function getFlowStep(flowSlug, segment, language = 'en') {
  return fetchJson(`/api/flows/${encodeURIComponent(flowSlug)}`
    + `/steps/${encodeURIComponent(segment)}?lang=${encodeURIComponent(language)}`);
}
